// root '../../../';

// dependencies
import { Child } from 'pym.js';
import Zion from './utils/Zion';
import '@mapbox/mapbox-gl-geocoder/dist/mapbox-gl-geocoder.css';
import { scaleDiverging, scaleLinear } from 'd3-scale';
import { interpolateRdYlBu, interpolateBrBG, interpolatePuOr } from 'd3-scale-chromatic';
import { select } from 'd3-selection';
import { extent } from 'd3-array';
import { timeFormat } from 'd3-time-format';
import { format } from 'd3-format';
// import meta, { footnote } from './data/meta.json';
// polyfills
// import fetch from 'unfetch';
import './utils/polyfills'; // (includes Array.findIndex && Array.find)


// styles
import './styles/graphic.scss';

/* develblock:start */
import meta from './data/meta.json';

const d3 = require('d3');

const mapboxgl = require('mapbox-gl');
const MapboxGeocoder = require('@mapbox/mapbox-gl-geocoder');

const pkg = require('../package.json');

if (process.env.NODE_ENV === 'development') {
  console.clear();
}

const interpolator = t => interpolatePuOr(0.1 + t * 0.8);

// console.log(interpolator);

// eslint-disable-next-line  no-use-before-define
window.pymChild = new Child({ id: pkg.name });

window.onload = () => {
  // necessary evil for right now

  if (process.env.NODE_ENV === 'development') {
    Object.keys(meta).forEach((id) => {
      const node = document.querySelector(`.${id}`);

      if (node) {
        node.innerHTML = meta[id];
      }
    });
  }

  const { pymChild } = window;
  pymChild.sendHeight();
  window.setTimeout(() => {
    pymChild.sendHeight();
  }, 800);

  // If embedded, send Zion page view event
  if (window.pymChild.parentUrl) {
    const zion = new Zion(pkg.name, pymChild.parentUrl.split('?')[0]);
  }
};

mapboxgl.accessToken = 'pk.eyJ1IjoiY25uZGlnaXRhbCIsImEiOiJja2V1M3Vyd3kwMDZrMnF0Njdhb3ZhZDk5In0.OURIYzTcS6ZzoWjXJzfANg';

const isMobile = window.innerWidth < 500;

const startLocation = ['-98.5', '39.8'];
const startZoom = isMobile ? 2.5 : 3.5;

const zoom = isMobile ? 4 : 5;
// const zoom = 5;

const map = new mapboxgl.Map({
  container: 'snow-forecast-map',
  // style: 'mapbox://styles/cnndigital/cl960ytee001215qu1uhorjlx',
  style: 'mapbox://styles/cnndigital/cmk30w3tz001801rz9bnse8v3',
  center: [-113.15381698577639, 41.37996886194373],
  zoom,
  minZoom: 3,
  maxZoom: 6,
  bearing: 0,
  scrollZoom: false,
  dragRotate: false,
  touchPitch: false,
  interactive: true,
  projection: 'albers',
});


updateZoom(map);
map.addControl(new mapboxgl.NavigationControl({
  showCompass: false,
}), 'top-right');

function createLegend(colorScale) {
  const legend = select('.legend-gradient');
  const legendWidth = legend.node().getBoundingClientRect().width;

  const legendGradient = legend
    .append('defs')
    .append('linearGradient')
    .attr('id', 'gradient')
    .attr('x1', '0')
    .attr('y1', '0')
    .attr('x2', '1')
    .attr('y2', '0');

  // Add more stops to show the diverging color scheme smoothly
  const gradientStops = [0, 25, 50, 75, 100, 125, 150, 175, 200];
  gradientStops.forEach((value) => {
    const offset = (value / 200) * 100;
    legendGradient.append('stop')
      .attr('offset', `${offset}%`)
      .attr('stop-color', colorScale(value))
      .attr('stop-opacity', 1);
  });

  legend.append('rect')
    .attr('width', '100%')
    .attr('height', 10)
    .attr('fill', 'url(#gradient)');

  const ticks = [0, 100, 200];
  const scaleExtent = extent(colorScale.domain());
  const legendScale = scaleLinear()
    .domain(scaleExtent)
    .range([0, legendWidth]);

  ticks.forEach((tick, i, arr) => {
    legend.append('text')
      .attr('x', `${legendScale(tick)}`)
      .attr('y', 28)
      .attr('text-anchor', 'middle')
      .attr('font-size', 12)
      .attr('font-weight', 400)
      .attr('fill', '#333')
      .text(`${tick}%`);

    legend.append('line')
      .attr('x1', () => {
        if (i === 0) {
          return 1;
        }
        if (i === arr.length - 1) {
          return legendWidth - 1;
        }
        return legendScale(tick);
      })
      .attr('x2', () => {
        if (i === 0) {
          return 1;
        }
        if (i === arr.length - 1) {
          return legendWidth - 1;
        }
        return legendScale(tick);
      })
      .attr('y1', 10)
      .attr('y2', 15)
      .attr('stroke', '#999')
      .attr('stroke-width', 1);
  });
}
map.on('load', () => {
  const firstLayer = map.getStyle().layers.find(layer => layer.id === 'background');

  d3.json('https://ix.cnn.io/data/snow-water-equivalent/swe-data.json').then((data) => {
    const colorScale = scaleDiverging()
      .domain([200, 100, 0])
      .interpolator(interpolator)
      .clamp(true);

    createLegend(colorScale);
    updateTimestamp(data.date_of_data);

    const geojson = {
      type: 'FeatureCollection',
      features: data.data.map(point => ({
        type: 'Feature',
        geometry: {
          type: 'Point',
          coordinates: [point.Longitude, point.Latitude],
        },
        properties: { ...point, color: colorScale(point.Percent_NRCS_1991_2020_Median) },
      })).sort((a, b) => Math.abs(a.properties.Percent_NRCS_1991_2020_Median - 100) - Math.abs(b.properties.Percent_NRCS_1991_2020_Median - 100)),
    };

    // Calculate bounds from points and limit panning
    const bounds = new mapboxgl.LngLatBounds();
    geojson.features.forEach((feature) => {
      bounds.extend(feature.geometry.coordinates);
    });
    // Add padding to bounds (in degrees)
    const padding = 2;
    const paddedBounds = [
      [bounds.getWest() - padding, bounds.getSouth() - padding],
      [bounds.getEast() + padding, bounds.getNorth() + padding],
    ];
    map.setMaxBounds(paddedBounds);

    map.addSource('snow-points', {
      type: 'geojson',
      data: geojson,
    });

    map.addLayer({
      id: 'snow-points',
      type: 'circle',
      source: 'snow-points',
      paint: {
        'circle-radius': 5,
        'circle-color': ['get', 'color'],
        'circle-stroke-width': 1,
        'circle-stroke-color': '#ffffff',
      },
    }, firstLayer.id);

    // Click interaction for station popups
    map.on('click', 'snow-points', (e) => {
      const coordinates = e.features[0].geometry.coordinates.slice();
      const { properties } = e.features[0];
      const color = colorScale(properties.Percent_NRCS_1991_2020_Median);
      new mapboxgl.Popup()
        .setLngLat(coordinates)
        .setHTML(`<div class="station-popup">
          <h3>${properties.Name}</h3>
          <p>${properties.County} County, ${properties.State}</p>
          <p>Elevation: ${format(',')(properties.Elevation_ft)} ft</p>
          <p><strong>${properties.Percent_NRCS_1991_2020_Median}%</strong> of median snowfall</p>
        </div>`)
        .addTo(map);
    });

    // Change cursor to pointer on hover
    map.on('mouseenter', 'snow-points', () => {
      map.getCanvas().style.cursor = 'pointer';
    });
    map.on('mouseleave', 'snow-points', () => {
      map.getCanvas().style.cursor = '';
    });

    map.on('moveend', () => {
      const center = map.getCenter();
      const zoom = map.getZoom();
      console.log('center', center, 'zoom', zoom);
    });
    pymChild.sendHeight();
  });
});


window.addEventListener('resize', (event) => {
  updateZoom(map);
});

function updateZoom(map) {
  const isMobile = window.innerWidth < 500;
  const zoom = isMobile ? 3 : 3.9;

  map.setZoom(zoom);

  pymChild.sendHeight();
}


d3.timeFormatDefaultLocale({
  periods: ['a.m.', 'p.m.'],
  days: [
    'Sunday',
    'Monday',
    'Tuesday',
    'Wednesday',
    'Thursday',
    'Friday',
    'Saturday',
  ],
  shortDays: ['Sun', 'Mon', 'Tues', 'Wed', 'Thurs', 'Fri', 'Sat'],
  months: [
    'January',
    'February',
    'March',
    'April',
    'May',
    'June',
    'July',
    'August',
    'September',
    'October',
    'November',
    'December',
  ],
  shortMonths: [
    'Jan.',
    'Feb.',
    'March',
    'April',
    'May',
    'June',
    'July',
    'Aug.',
    'Sept.',
    'Oct.',
    'Nov.',
    'Dec.',
  ],
});

function updateTimestamp(date) {
  // const formattedDate = timeFormat('%b. %d, %Y');
  const justDate = date.replace(', end of day', '');
  d3.select('#forecast-map-timestamp')
    .text(`Note: Data as of ${justDate}. ${meta.footnote}`);
}
